import type { Translate } from "@khazzin/i18n";
import { useId } from "react";
import { Label } from "../primitives/Field";
import { Input } from "../primitives/Input";
import { Select } from "../primitives/Select";
import { cx } from "../styles";
import {
	DATE_RANGE_PRESETS,
	type DateRange,
	type DateRangePreset,
	isIsoDate,
	presetOf,
	presetRange,
} from "./dateRange";

/**
 * A date range with the reporting presets (EP05-S09, `13/10` §3): a preset
 * select, and From / To date fields that always show what the preset means.
 * Typing a date makes the range custom; choosing a preset fills both dates.
 *
 * In `ui/` because the Session Log is its first user and the Analytics
 * screens (EP-13) use the same presets.
 *
 * The range is two org-local dates, both inclusive; `today` is the
 * organisation's today, which the caller computes from its timezone.
 */
export interface DateRangePickerProps {
	value: DateRange;
	onChange: (range: DateRange) => void;
	/** The organisation's today, `YYYY-MM-DD`. */
	today: string;
	t: Translate;
	className?: string;
}

export function DateRangePicker({ value, onChange, today, t, className }: DateRangePickerProps) {
	const id = useId();
	const preset = presetOf(value, today);
	const options = [
		...DATE_RANGE_PRESETS.map((p) => ({ value: p, label: t(`ui.dateRange.presets.${p}`) })),
		{ value: "custom", label: t("ui.dateRange.presets.custom") },
	];

	function setDate(field: "from" | "to", next: string) {
		// A half-typed date is not a range yet; the field keeps it until whole.
		if (!isIsoDate(next)) {
			return;
		}
		const range = { ...value, [field]: next };
		// An inverted range follows the date just typed rather than being refused.
		if (range.from > range.to) {
			if (field === "from") {
				range.to = next;
			} else {
				range.from = next;
			}
		}
		onChange(range);
	}

	return (
		<fieldset className={cx("flex flex-wrap items-end gap-2.5", className)}>
			<legend className="sr-only">{t("ui.dateRange.legend")}</legend>
			<div className="flex min-w-40 flex-col gap-1">
				<Label htmlFor={`${id}-preset`}>{t("ui.dateRange.preset")}</Label>
				<Select
					id={`${id}-preset`}
					options={options}
					value={preset}
					onValueChange={(next) => {
						if (next !== "custom") {
							onChange(presetRange(next as Exclude<DateRangePreset, "custom">, today));
						}
					}}
				/>
			</div>
			<div className="flex flex-col gap-1">
				<Label htmlFor={`${id}-from`}>{t("ui.dateRange.from")}</Label>
				<Input
					id={`${id}-from`}
					type="date"
					value={value.from}
					max={today}
					onChange={(event) => setDate("from", event.target.value)}
				/>
			</div>
			<div className="flex flex-col gap-1">
				<Label htmlFor={`${id}-to`}>{t("ui.dateRange.to")}</Label>
				<Input
					id={`${id}-to`}
					type="date"
					value={value.to}
					max={today}
					onChange={(event) => setDate("to", event.target.value)}
				/>
			</div>
		</fieldset>
	);
}
